import { useEffect, useState, useMemo } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { featuredPosts } from "@/data/featuredPosts";

// Keyword → content mapping for intelligent URL-based suggestions
const contentMap: { keywords: string[]; label: string; url: string; description: string }[] = [
  { keywords: ["aeo", "answer-engine", "answer engine", "citation"], label: "AEO Agency London", url: "/services/aeo-agency-london/", description: "Get cited in ChatGPT, Gemini, Perplexity and Google AI Mode" },
  { keywords: ["geo", "generative", "ai-search", "ai search"], label: "GEO & AI Search", url: "/services/geo-agency-london/", description: "Generative Engine Optimization for B2B brands" },
  { keywords: ["seo", "organic", "search-engine", "ranking"], label: "SEO, AEO & GEO", url: "/services/seo-aeo-geo/", description: "Full-funnel organic visibility across traditional and AI search" },
  { keywords: ["ppc", "performance-max", "google-ads", "paid", "ads"], label: "PPC & Performance Max", url: "/services/ppc-performance-max/", description: "Higher ROAS, lower CPA, full-funnel attribution" },
  { keywords: ["agentic", "agent", "lead-gen", "automation", "pipeline"], label: "Agentic AI Lead Gen", url: "/services/gemini-agentic-ai/", description: "Autonomous pipeline that runs 24/7 without headcount" },
  { keywords: ["website", "web", "schema", "structured-data"], label: "AI Websites & Schema", url: "/services/ai-websites/", description: "Entity-rich sites built for AI crawlers and humans" },
  { keywords: ["strategy", "growth", "marketing", "abm"], label: "AI Marketing Strategy", url: "/services/ai-marketing-strategy/", description: "Senior-led growth strategy combining AI + paid + organic" },
  { keywords: ["prompt", "improver", "framework", "co-star", "risen"], label: "AI Prompt Improver", url: "/tools/ai-prompt-improver/", description: "Free tool: apply frameworks and clean prompts for any AI" },
  { keywords: ["token", "calculator", "cost", "pricing", "llm"], label: "AI Token Calculator", url: "/tools/ai-token-calculator/", description: "Free tool: compare 30+ LLM costs instantly" },
  { keywords: ["utm", "campaign", "builder", "tracking"], label: "UTM Campaign Builder", url: "/tools/utm-builder/", description: "Free tool: generate UTM-tagged URLs for campaign tracking" },
  { keywords: ["keyword", "ppc-keyword", "processor", "negative"], label: "PPC & Performance Max", url: "/services/ppc-performance-max/", description: "Explore PPC and Performance Max support for measurement, query quality and campaign strategy" },
  { keywords: ["audit", "score", "visibility", "free"], label: "Free AI Growth Audit", url: "/free-ai-growth-audit/", description: "See where you rank in ChatGPT, Gemini & Google AI Mode" },
  { keywords: ["blog", "article", "post", "news", "guide"], label: "AI Marketing Blog", url: "/blog/", description: "Expert analysis on AI search, agentic marketing and B2B growth" },
  { keywords: ["case", "results", "study", "studies", "proof"], label: "Case Studies", url: "/case-studies/", description: "Pipeline-measured results from real client engagements" },
  { keywords: ["about", "modi", "elnadi", "team", "founder"], label: "About Modi Elnadi", url: "/modi-elnadi/", description: "Senior AI marketing strategist, 10+ years in B2B growth" },
  { keywords: ["chatgpt", "openai", "gpt", "modes"], label: "ChatGPT Modes Guide", url: "/blog/chatgpt-modes-explained-work-codex-remote-cloud-guide-2026/", description: "Complete guide to all 7 ChatGPT modes for 2026" },
  { keywords: ["singularity", "consciousness", "agi", "asi"], label: "AI Singularity Essay", url: "/blog/ai-singularity-consciousness-capability-controversial-2026/", description: "Why capability matters more than consciousness" },
  { keywords: ["waymo", "autonomous", "driving", "robotaxi", "self-driving"], label: "Waymo & Autonomous AI", url: "/blog/waymo-robotaxi-20-million-rides-autonomous-driving-safety-2026/", description: "20 million rides with 92% fewer pedestrian injuries" },
  { keywords: ["flock", "surveillance", "camera", "privacy", "police"], label: "Flock Cameras Exposed", url: "/blog/flock-cameras-ai-surveillance-police-abuse-privacy-2026/", description: "50 officers caught stalking partners through AI surveillance" },
  { keywords: ["google", "ai-mode", "gemini"], label: "Google AI Mode Optimisation", url: "/services/google-ai-mode-optimisation/", description: "Appear in Google's AI Mode answers for your buying queries" },
];

function getSuggestionsFromPath(path: string): typeof contentMap {
  const normalised = path.toLowerCase().replace(/[/_-]/g, " ").trim();
  const words = normalised.split(/\s+/).filter(w => w.length > 2);

  if (words.length === 0) return [];

  const scored = contentMap.map((item) => {
    let score = 0;
    for (const word of words) {
      for (const keyword of item.keywords) {
        if (keyword.includes(word) || word.includes(keyword)) {
          score += keyword === word ? 3 : 1;
        }
      }
    }
    return { ...item, score };
  });

  return scored
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);
}

export default function NotFound() {
  const [searchQuery, setSearchQuery] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  const suggestions = useMemo(() => {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    return getSuggestionsFromPath(path);
  }, []);

  const placeholders = [
    "Try: prompt engineering frameworks...",
    "Try: AI token calculator...",
    "Try: AEO agency London...",
    "Try: ChatGPT modes explained...",
    "Try: Waymo autonomous driving...",
    "Try: agentic AI lead generation...",
    "Try: Free AI Visibility Score...",
  ];

  useEffect(() => {
    // Signal to crawlers this is a genuine 404
    document.title = "404 Page Not Found | Integrated.Social";

    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta") as HTMLMetaElement;
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", "noindex, follow");

    // Keep the dedicated 404 document’s canonical stable after SPA navigation.
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://integrated.social/404/";
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#0F1419] flex items-center justify-center px-4">
        <div className="max-w-2xl text-center py-32">
          <div className="text-[#FF8C00] text-xs font-mono tracking-widest uppercase mb-4">
            404 - Page Not Found
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl text-white mb-6 leading-tight">
            This page doesn't exist.
          </h1>
          <p className="text-white/60 text-lg leading-relaxed mb-10">
            The URL you followed may be outdated, mistyped, or the content may have moved.
            Try one of the links below to find what you need.
          </p>

          {/* AI-powered URL suggestions */}
          {suggestions.length > 0 && (
            <div className="mb-8 max-w-lg mx-auto text-left">
              <div className="flex items-center gap-2 mb-3 justify-center">
                <span className="text-emerald-400 text-xs font-mono tracking-wider uppercase">
                  Based on the URL you tried, you might be looking for:
                </span>
              </div>
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <a
                    key={s.url}
                    href={s.url}
                    className="group flex items-start gap-3 bg-white/5 border border-white/10 rounded-sm p-3 hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-colors"
                  >
                    <span className="text-emerald-400 text-lg mt-0.5">→</span>
                    <div>
                      <span className="text-white text-sm font-medium group-hover:text-emerald-400 transition-colors">
                        {s.label}
                      </span>
                      <p className="text-white/40 text-xs mt-0.5">{s.description}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Search bar */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (searchQuery.trim()) {
                window.location.href = `/blog/search?q=${encodeURIComponent(searchQuery.trim())}`;
              }
            }}
            className="mb-10 max-w-md mx-auto"
          >
            <div className="flex gap-2">
              <input
                type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={placeholders[placeholderIndex]}
                className="flex-1 bg-white/5 border border-white/20 rounded-sm px-4 py-3 text-white placeholder:text-white/40 placeholder:transition-opacity text-sm focus:outline-none focus:border-[#FF8C00]/60 transition-colors"
                aria-label="Search the site"
              />
              <button
                type="submit"
                className="bg-[#FF8C00] text-[#0F1419] px-5 py-3 text-sm font-semibold rounded-sm hover:bg-[#E67E00] transition-colors"
              >
                Search
              </button>
            </div>
          </form>

          {/* Quick links */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <a
              href="/"
              className="bg-[#FF8C00] text-[#0F1419] px-6 py-3 text-sm font-semibold rounded-sm hover:bg-[#E67E00] transition-colors"
            >
              Go to Homepage
            </a>
            <a
              href="/free-ai-growth-audit/"
              className="bg-emerald-600/20 text-emerald-400 px-6 py-3 text-sm font-semibold rounded-sm hover:bg-emerald-600/30 transition-colors border border-emerald-500/30"
            >
              Check my AI visibility
            </a>
            <a
              href="/case-studies/"
              className="bg-white/10 text-white px-6 py-3 text-sm font-semibold rounded-sm hover:bg-white/20 transition-colors border border-white/20"
            >
              Case Studies
            </a>
          </div>

          {/* Secondary links */}
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="/blog/"
              className="text-white/60 px-4 py-2 text-sm hover:text-white transition-colors border border-white/10 rounded-sm hover:border-white/30"
            >
              Blog
            </a>
            <a
              href="/services/gemini-agentic-ai/"
              className="text-white/60 px-4 py-2 text-sm hover:text-white transition-colors border border-white/10 rounded-sm hover:border-white/30"
            >
              Agentic AI
            </a>
            <a
              href="/tools/ai-prompt-improver/"
              className="text-white/60 px-4 py-2 text-sm hover:text-white transition-colors border border-white/10 rounded-sm hover:border-white/30"
            >
              Free Tools
            </a>
            <a
              href="/services/seo-aeo-geo/"
              className="text-white/60 px-4 py-2 text-sm hover:text-white transition-colors border border-white/10 rounded-sm hover:border-white/30"
            >
              SEO & AEO
            </a>
          </div>

          {/* Trending posts */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <p className="text-[#FF8C00] text-xs font-mono tracking-widest uppercase mb-5">
              Trending Now
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
              {featuredPosts.slice(0, 3).map((post) => (
                <a
                  key={post.slug}
                  href={`/blog/${post.slug}/`}
                  className="group block bg-white/5 border border-white/10 rounded-sm p-4 hover:border-[#FF8C00]/40 hover:bg-white/[0.07] transition-colors"
                >
                  <div className="flex gap-1 flex-wrap mb-2">
                    {post.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-[9px] font-mono uppercase text-[#FF8C00]/70 bg-[#FF8C00]/10 px-1.5 py-0.5 rounded-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-white text-sm font-medium leading-snug line-clamp-2 group-hover:text-[#FF8C00] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-white/40 text-xs mt-2 line-clamp-2">
                    {post.excerpt}
                  </p>
                </a>
              ))}
            </div>
          </div>

          <p className="text-white/30 text-sm mt-10">
            Looking for something specific?{" "}
            <a aria-label="Email Integrated.Social at info@integrated.social" href="mailto:info@integrated.social" className="text-[#FF8C00] hover:underline">
              Email us
            </a>{" "}
            and we'll help you find it.
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
}
